import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  IconButton,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  Alert,
  Checkbox,
  Tooltip,
  Stack,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import UndoIcon from '@mui/icons-material/Undo';
import AddIcon from '@mui/icons-material/Add';
import PrintIcon from '@mui/icons-material/Print';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import { useSnackbar } from 'notistack';
import api from '../../services/api';
import tesoreriaAplicacionesService, {
  CreditoConSaldoPendiente,
  DebitoConSaldoPendiente,
  AplicacionRecibo,
  DocumentoAplicar,
  AplicacionCreditoEnDebito,
  getDebitosParaAplicarCliente,
  getCreditosProveedorParaAplicaciones,
  enriquecerCreditoProveedorConSaldo,
} from '../../services/tesoreriaAplicacionesService';
import PDFViewer from '../shared/PDFViewer';
import { formatCurrency } from '../../utils/formatters';
import { getFileNameFromContentDisposition } from '../../utils/contentDisposition';
import { getApiErrorMessage } from '../../utils/apiError';
import { tipoToPrefijo, esDevolucionCobranzaCliente } from '../../constants/tiposComprobante';
import { formatComprobanteTesoreria } from '../../utils/formatters';

function claveImputaDesdeDebito(app: AplicacionCreditoEnDebito): string {
  return `${app.nroIdformPagoCobro}|${app.nroMovCaja}|${app.fechaEmisionDebito}`;
}

const formatterFecha = new Intl.DateTimeFormat('es-AR', { dateStyle: 'short' });

function formatComprobante(letra?: string, nroSuc?: number, nroComprobante?: number): string {
  if (nroSuc == null || nroComprobante == null) return '-';
  const L = (letra || '').trim().toUpperCase() || '-';
  const s = String(nroSuc).padStart(4, '0');
  const c = String(nroComprobante).padStart(8, '0');
  return `${L}-${s}-${c}`;
}

/** Mismo criterio que cuenta corriente: prefijo tipo + letra + sucursal + número (ej. NCX-0001-02062026) */
function formatNumeroCredito(cred?: {
  nroTipoFormulario?: string;
  letra?: string;
  nroSuc?: number;
  nroComprobante?: number;
} | null): string {
  if (!cred) return '-';
  const letra = (cred.letra ?? '').trim().toUpperCase();
  if (!letra && cred.nroSuc == null && (cred.nroComprobante ?? 0) <= 0) return '-';
  const tipo = (cred.nroTipoFormulario ?? '').toString().trim();
  if (tipo === '60') {
    return formatComprobanteTesoreria(cred.letra, cred.nroSuc, cred.nroComprobante);
  }
  const prefijo = tipoToPrefijo(cred.nroTipoFormulario);
  const s = String(cred.nroSuc ?? 0).padStart(4, '0');
  const c = String(cred.nroComprobante ?? 0).padStart(8, '0');
  return `${prefijo}${letra}-${s}-${c}`;
}

function formatNumeroDebito(deb?: {
  nroTipoFormulario?: string;
  letra?: string;
  nroSuc?: number;
  nroComprobante?: number;
} | null): string {
  if (!deb) return '-';
  const letra = (deb.letra ?? '').trim().toUpperCase();
  if (!letra && deb.nroSuc == null && (deb.nroComprobante ?? 0) <= 0) return '-';
  const tipo = (deb.nroTipoFormulario ?? '').toString().trim();
  if (tipo === '60') {
    return formatComprobanteTesoreria(deb.letra, deb.nroSuc, deb.nroComprobante);
  }
  const prefijo = tipoToPrefijo(deb.nroTipoFormulario);
  const s = String(deb.nroSuc ?? 0).padStart(4, '0');
  const c = String(deb.nroComprobante ?? 0).padStart(8, '0');
  return `${prefijo}${letra}-${s}-${c}`;
}

const etiquetaComprobanteDebito = (nroTipo?: string | null): string => {
  const t = (nroTipo ?? '').toString().trim();
  if (t === '1') return 'Factura';
  if (t === '2') return 'Nota de débito';
  if (t === '60') return 'Devolución';
  return 'Comprobante de débito';
};

function BannerComprobanteSaldo({
  etiqueta,
  numero,
  saldoPendiente,
  mostrarSaldo = true,
}: {
  etiqueta: string;
  numero: string;
  saldoPendiente: number;
  mostrarSaldo?: boolean;
}) {
  if (!numero || numero === '-') return null;
  return (
    <Alert severity="info" sx={{ mb: 2 }}>
      <Typography variant="body2" component="span">
        <strong>{etiqueta}:</strong> {numero}
        {mostrarSaldo && saldoPendiente > 0 && (
          <>
            {' '}
            — <strong>Saldo pendiente:</strong> {formatCurrency(saldoPendiente)}
          </>
        )}
      </Typography>
    </Alert>
  );
}

function roundMoney(value: number): number {
  // Evita problemas de floating point (p.ej. 5000.0002) al operar con decimales.
  return Math.round(value * 100) / 100;
}

interface AplicarSaldoReciboDialogProps {
  open: boolean;
  onClose: () => void;
  reciboId?: number;
  /** Cliente (C) o proveedor (P): en Formulario es NroCliente en ambos casos */
  nroCliente: number;
  nombreCliente: string;
  onSuccess?: () => void;
  /** Si true, permite seleccionar un recibo de la lista de créditos (para uso desde ResumenCuenta) */
  selectorMode?: boolean;
  /** Compras: órdenes de pago / NC; cobranza: recibos / NC */
  variant?: 'cliente' | 'proveedor';
  /** Vista desde un débito de cliente (FC, ND, devolución): créditos que imputaron en este documento */
  nroFormularioDebitoCliente?: number;
  nroTipoFormularioDebitoCliente?: string;
  /** Si el crédito/recibo ya fue anulado (no permite aplicar saldo) */
  reciboAnulado?: boolean;
  /** Número de comprobante del crédito (ej. desde resumen de cuenta); si no se envía, se arma desde el crédito cargado */
  numeroComprobanteCredito?: string;
  /** Número de comprobante del débito (factura, devolución, ND) desde resumen de cuenta */
  numeroComprobanteDebito?: string;
  /** Tipo del crédito (3 NC, 4 REC, 6 OP) cuando se abre desde listados */
  nroTipoFormularioCredito?: string;
  /** Importe total del crédito si no está en el listado unificado (p. ej. NC desde su listado) */
  importeTotalCredito?: number;
  /** Datos del comprobante cuando el crédito ya no tiene saldo (no viene en el listado con pendiente) */
  letraCredito?: string;
  nroSucCredito?: number;
  nroComprobanteCreditoNum?: number;
  /** OP/NC ya visibles en el resumen de cuenta (misma pantalla) */
  creditosConocidos?: CreditoConSaldoPendiente[];
  debitosConocidos?: DebitoConSaldoPendiente[];
}

const AplicarSaldoReciboDialog: React.FC<AplicarSaldoReciboDialogProps> = ({
  open,
  onClose,
  reciboId: reciboIdProp,
  nroCliente,
  nombreCliente,
  onSuccess,
  selectorMode = false,
  variant = 'cliente',
  nroFormularioDebitoCliente,
  nroTipoFormularioDebitoCliente,
  reciboAnulado = false,
  numeroComprobanteCredito: numeroComprobanteCreditoProp,
  numeroComprobanteDebito: numeroComprobanteDebitoProp,
  nroTipoFormularioCredito: nroTipoFormularioCreditoProp,
  importeTotalCredito: importeTotalCreditoProp,
  letraCredito: letraCreditoProp,
  nroSucCredito: nroSucCreditoProp,
  nroComprobanteCreditoNum: nroComprobanteCreditoNumProp,
  creditosConocidos,
  debitosConocidos,
}) => {
  const isProveedor = variant === 'proveedor';
  const modoVistaDebitoProp = (nroFormularioDebitoCliente ?? 0) > 0;
  const vistaAbiertaRef = useRef({
    modoVistaDebito: false,
    selectorMode: false,
  });
  if (open) {
    vistaAbiertaRef.current = {
      modoVistaDebito: modoVistaDebitoProp,
      selectorMode,
    };
  }
  /** Durante el fade-out el padre ya limpió los IDs; sin esto se ve la lista de créditos detrás. */
  const modoVistaDebito = open ? modoVistaDebitoProp : vistaAbiertaRef.current.modoVistaDebito;
  const selectorModeVista = open ? selectorMode : vistaAbiertaRef.current.selectorMode;
  const [reciboSeleccionado, setReciboSeleccionado] = useState<number | null>(null);
  const reciboId = reciboIdProp ?? reciboSeleccionado ?? 0;
  const { enqueueSnackbar } = useSnackbar();
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [aplicaciones, setAplicaciones] = useState<AplicacionRecibo[]>([]);
  const [aplicacionesCreditoEnDebito, setAplicacionesCreditoEnDebito] = useState<AplicacionCreditoEnDebito[]>([]);
  const [creditos, setCreditos] = useState<CreditoConSaldoPendiente[]>([]);
  const [creditoRecibo, setCreditoRecibo] = useState<CreditoConSaldoPendiente | null>(null);
  const [debitos, setDebitos] = useState<DebitoConSaldoPendiente[]>([]);
  const [debitosParaAplicar, setDebitosParaAplicar] = useState<DebitoConSaldoPendiente[]>([]);
  const [debitoClienteActual, setDebitoClienteActual] = useState<DebitoConSaldoPendiente | null>(null);
  const [desaplicando, setDesaplicando] = useState<{ nroIdformulario: number; fechaEmision: string; nroMovCaja: number } | null>(null);
  const [desaplicandoDebitoClave, setDesaplicandoDebitoClave] = useState<string | null>(null);
  const [aplicando, setAplicando] = useState(false);
  const [montosAplicar, setMontosAplicar] = useState<Record<number, number>>({});
  const [seleccionados, setSeleccionados] = useState<Set<number>>(new Set());

  const saldoPendienteRecibo = roundMoney(creditoRecibo?.saldoPendiente ?? 0);
  const numeroComprobanteCredito =
    (numeroComprobanteCreditoProp?.trim() || formatNumeroCredito(creditoRecibo)) || '-';
  const saldoPendienteDebito = roundMoney(debitoClienteActual?.montoPendiente ?? 0);
  const numeroComprobanteDebito =
    (numeroComprobanteDebitoProp?.trim() || formatNumeroDebito(debitoClienteActual)) || '-';
  const enSubvistaSelector = selectorModeVista && reciboSeleccionado != null && !modoVistaDebito;

  const mergeDebitosConocidos = useCallback(
    (aplicables: DebitoConSaldoPendiente[]): DebitoConSaldoPendiente[] => {
      const known = (debitosConocidos ?? []).filter(
        (d) => d.nroFormulario > 0 && roundMoney(d.montoPendiente ?? 0) > 0
      );
      if (known.length === 0) return aplicables;
      const merged = [...aplicables];
      for (const d of known) {
        if (!merged.some((x) => x.nroFormulario === d.nroFormulario)) {
          merged.unshift(d);
        }
      }
      return merged;
    },
    [debitosConocidos]
  );

  const handleVolverAlSelector = () => {
    setReciboSeleccionado(null);
    setCreditoRecibo(null);
    setAplicaciones([]);
    setMontosAplicar({});
    setSeleccionados(new Set());
    setTab(0);
  };

  // Viewer de PDF para "Imprimir PDF" (misma lógica que en /tesoreria/recibos)
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [selectedCreditoPdfId, setSelectedCreditoPdfId] = useState<number | null>(null);
  const [pdfFileName, setPdfFileName] = useState<string | null>(null);

  const handleImprimirPDF = async (nroFormulario: number) => {
    try {
      const urlPdf = isProveedor
        ? `/api/Tesoreria/ordendepago/${nroFormulario}/pdf`
        : `/api/tesoreria/recibos/${nroFormulario}/pdf`;
      const response = await api.get(urlPdf, {
        responseType: 'blob',
      });

      const cd = (response.headers as any)?.['content-disposition'] as string | undefined;
      const backendFileName = getFileNameFromContentDisposition(cd);
      setPdfFileName(backendFileName);

      const url = window.URL.createObjectURL(new Blob([response.data]));
      setPdfUrl(url);
      setSelectedCreditoPdfId(nroFormulario);
    } catch {
      enqueueSnackbar(
        isProveedor ? 'Error al generar el PDF de la orden de pago' : 'Error al generar el PDF del recibo',
        { variant: 'error' }
      );
    }
  };

  const handleClosePDF = () => {
    if (pdfUrl) {
      window.URL.revokeObjectURL(pdfUrl);
    }
    setPdfUrl(null);
    setSelectedCreditoPdfId(null);
    setPdfFileName(null);
  };

  const loadData = useCallback(async () => {
    if (!open || !nroCliente) return;

    if (modoVistaDebitoProp && nroFormularioDebitoCliente) {
      try {
        setLoading(true);
        const [creditosRes, debitosRes, apps] = await Promise.all([
          (isProveedor
            ? getCreditosProveedorParaAplicaciones(nroCliente)
            : tesoreriaAplicacionesService.getCreditosConSaldoPendiente(nroCliente)
          ).catch(() => [] as CreditoConSaldoPendiente[]),
          (isProveedor
            ? tesoreriaAplicacionesService.getDebitosProveedorConSaldoPendiente(nroCliente)
            : tesoreriaAplicacionesService.getDebitosConSaldoPendiente(nroCliente)
          ).catch(() => [] as DebitoConSaldoPendiente[]),
          isProveedor
            ? tesoreriaAplicacionesService.getImputacionesCreditoEnDebitoProveedor(
                nroCliente,
                nroFormularioDebitoCliente,
                {
                  numeroComprobanteDebito: numeroComprobanteDebitoProp,
                  creditos: creditosConocidos,
                }
              )
            : tesoreriaAplicacionesService.getImputacionesCreditoEnDebitoCliente(
                nroCliente,
                nroFormularioDebitoCliente,
                nroTipoFormularioDebitoCliente
              ),
        ]);
        const debitosAplicables = isProveedor
          ? debitosRes.filter((d) => roundMoney(d.montoPendiente ?? 0) > 0)
          : await getDebitosParaAplicarCliente(nroCliente, debitosRes, creditosRes);
        const debitoActual =
          debitosAplicables.find((d) => d.nroFormulario === nroFormularioDebitoCliente) ??
          debitosRes.find((d) => d.nroFormulario === nroFormularioDebitoCliente) ??
          null;
        setAplicacionesCreditoEnDebito(apps);
        setAplicaciones([]);
        setCreditos([]);
        setDebitos(debitosRes);
        setDebitosParaAplicar([]);
        setCreditoRecibo(null);
        setDebitoClienteActual(debitoActual);
        setMontosAplicar({});
        setSeleccionados(new Set());
      } catch (error) {
        console.error('Error al cargar imputaciones:', error);
        enqueueSnackbar(
          getApiErrorMessage(error, 'No se pudieron cargar las imputaciones.'),
          { variant: 'error' }
        );
      } finally {
        setLoading(false);
      }
      return;
    }

    if (!reciboId && !selectorMode) return;
    try {
      setLoading(true);
      const [creditosRes, debitosRes] = await Promise.all(
        isProveedor
          ? [
              getCreditosProveedorParaAplicaciones(nroCliente),
              tesoreriaAplicacionesService.getDebitosProveedorConSaldoPendiente(nroCliente),
            ]
          : [
              tesoreriaAplicacionesService.getCreditosConSaldoPendiente(nroCliente),
              tesoreriaAplicacionesService.getDebitosConSaldoPendiente(nroCliente),
            ]
      );
      setCreditos(creditosRes);
      setDebitos(debitosRes);
      const debitosAplicables = isProveedor
        ? debitosRes.filter((d) => roundMoney(d.montoPendiente ?? 0) > 0)
        : await getDebitosParaAplicarCliente(nroCliente, debitosRes, creditosRes);
      setDebitosParaAplicar(mergeDebitosConocidos(debitosAplicables));
      setAplicacionesCreditoEnDebito([]);
      setDebitoClienteActual(null);
      if (reciboId > 0) {
        const tipoCred =
          (nroTipoFormularioCreditoProp ?? '').trim() ||
          creditosRes.find((c) => c.nroFormulario === reciboId)?.nroTipoFormulario;
        const aplicacionesRes = isProveedor
          ? await tesoreriaAplicacionesService.getAplicacionesCreditoProveedor(reciboId, tipoCred)
          : await tesoreriaAplicacionesService.getAplicacionesRecibo(reciboId);
        setAplicaciones(aplicacionesRes);
        let cred = creditosRes.find((c) => c.nroFormulario === reciboId) ?? null;
        if (!cred && reciboId > 0) {
          const tipoFallback =
            (nroTipoFormularioCreditoProp ?? '').trim() ||
            (isProveedor ? '6' : '3');
          const totalAplicado = roundMoney(
            aplicacionesRes.reduce((acc, a) => acc + roundMoney(a.montoCancelado ?? 0), 0)
          );
          const importe = roundMoney(importeTotalCreditoProp ?? 0);
          const saldoPendiente =
            importe > 0 ? Math.max(0, roundMoney(importe - totalAplicado)) : 0;
          cred = {
            nroFormulario: reciboId,
            nroTipoFormulario: tipoFallback,
            letra: (letraCreditoProp ?? '').trim(),
            nroSuc: nroSucCreditoProp ?? 0,
            nroComprobante: nroComprobanteCreditoNumProp,
            importeTotal: importe > 0 ? importe : undefined,
            totalAplicado,
            saldoPendiente,
          };
        }
        if (isProveedor && cred) {
          cred = await enriquecerCreditoProveedorConSaldo(cred);
        }
        setCreditoRecibo(cred);
      } else {
        setAplicaciones([]);
        setCreditoRecibo(null);
      }
      setMontosAplicar({});
      setSeleccionados(new Set());
    } catch (error) {
      console.error('Error al cargar datos:', error);
      enqueueSnackbar(
        getApiErrorMessage(error, 'No se pudieron cargar los datos.'),
        { variant: 'error' }
      );
    } finally {
      setLoading(false);
    }
  }, [
    open,
    nroCliente,
    reciboId,
    selectorMode,
    enqueueSnackbar,
    isProveedor,
    modoVistaDebitoProp,
    nroFormularioDebitoCliente,
    nroTipoFormularioDebitoCliente,
    nroTipoFormularioCreditoProp,
    importeTotalCreditoProp,
    letraCreditoProp,
    nroSucCreditoProp,
    nroComprobanteCreditoNumProp,
    numeroComprobanteDebitoProp,
    creditosConocidos,
    mergeDebitosConocidos,
  ]);

  useEffect(() => {
    if (open) {
      if (modoVistaDebitoProp) {
        setReciboSeleccionado(null);
        setTab(0);
      } else if (!selectorMode && reciboIdProp != null) {
        setReciboSeleccionado(null);
        // No seteamos tab aún: esperamos a que se cargue el crédito y su saldo pendiente.
        setTab(0);
      }
      loadData();
    }
  }, [open, loadData, reciboId, selectorMode, reciboIdProp, modoVistaDebitoProp]);

  useEffect(() => {
    // Con crédito elegido (directo o desde selector), ir a "Aplicar saldo" si hay pendiente.
    if (!open || reciboId <= 0 || modoVistaDebitoProp) return;
    if (selectorMode && reciboSeleccionado == null) return;

    setTab(saldoPendienteRecibo > 0 ? 1 : 0);
  }, [open, selectorMode, reciboId, reciboSeleccionado, saldoPendienteRecibo, modoVistaDebitoProp]);

  const resetEstadoAlCerrar = () => {
    setReciboSeleccionado(null);
    setAplicacionesCreditoEnDebito([]);
    setAplicaciones([]);
    setDebitoClienteActual(null);
    setDesaplicandoDebitoClave(null);
    setCreditos([]);
    setDebitos([]);
    setDebitosParaAplicar([]);
    setCreditoRecibo(null);
  };

  const handleDesaplicarCreditoEnDebito = async (app: AplicacionCreditoEnDebito) => {
    if (!nroFormularioDebitoCliente) return;
    const clave = claveImputaDesdeDebito(app);
    if (
      !window.confirm(
        `¿Desaplicar ${formatCurrency(app.montoCancelado ?? 0)} del crédito ${tipoToPrefijo(app.nroTipoFormularioCredito)} ${formatComprobante(app.letra, app.nroSuc, app.nroComprobante)}?`
      )
    ) {
      return;
    }
    try {
      setDesaplicandoDebitoClave(clave);
      const desaplicarRequest = {
        nroIdformulario: nroFormularioDebitoCliente,
        fechaEmision: app.fechaEmisionDebito,
        nroMovCaja: app.nroMovCaja,
      };
      if (isProveedor) {
        await tesoreriaAplicacionesService.desaplicarPagoProveedor(
          app.nroIdformPagoCobro,
          desaplicarRequest,
          app.nroTipoFormularioCredito
        );
      } else {
        await tesoreriaAplicacionesService.desaplicarRecibo(app.nroIdformPagoCobro, desaplicarRequest);
      }
      enqueueSnackbar('Aplicación desaplicada correctamente.', { variant: 'success' });
      await loadData();
      onSuccess?.();
    } catch (error: any) {
      const msg = error.response?.data?.message || 'Error al desaplicar.';
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setDesaplicandoDebitoClave(null);
    }
  };

  const handleDesaplicar = async (app: AplicacionRecibo) => {
    const key = { nroIdformulario: app.nroIdformulario, fechaEmision: app.fechaEmision, nroMovCaja: app.nroMovCaja };
    if (
      !window.confirm(
        `¿Desaplicar ${formatCurrency(app.montoCancelado ?? 0)} del documento ${formatComprobante(app.letra, app.nroSuc ?? 0, app.nroComprobante)}?`
      )
    ) {
      return;
    }
    try {
      setDesaplicando(key);
      if (isProveedor) {
        await tesoreriaAplicacionesService.desaplicarPagoProveedor(
          reciboId,
          {
            nroIdformulario: key.nroIdformulario,
            fechaEmision: key.fechaEmision,
            nroMovCaja: key.nroMovCaja,
          },
          creditoRecibo?.nroTipoFormulario ?? nroTipoFormularioCreditoProp
        );
      } else {
        await tesoreriaAplicacionesService.desaplicarRecibo(reciboId, {
          nroIdformulario: key.nroIdformulario,
          fechaEmision: key.fechaEmision,
          nroMovCaja: key.nroMovCaja,
        });
      }
      enqueueSnackbar('Aplicación desaplicada correctamente.', { variant: 'success' });
      await loadData();
      onSuccess?.();
    } catch (error: any) {
      const msg = error.response?.data?.message || 'Error al desaplicar.';
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setDesaplicando(null);
    }
  };

  const toggleSeleccion = (d: DebitoConSaldoPendiente) => {
    const nroForm = d.nroFormulario;
    setSeleccionados((prev) => {
      const next = new Set(prev);
      if (next.has(nroForm)) {
        next.delete(nroForm);
        setMontosAplicar((m) => {
          const copy = { ...m };
          delete copy[nroForm];
          return copy;
        });
      } else {
        next.add(nroForm);
        // Cap: el monto inicial para esta fila debe considerar el saldo ya consumido por
        // las facturas previamente seleccionadas (prioridad click order).
        setMontosAplicar((m) => {
          const sumaSeleccionados = Array.from(prev).reduce((sum, nro) => {
            const v = m[nro];
            return sum + (typeof v === 'number' ? v : 0);
          }, 0);

          const saldoRestantePrevio = roundMoney(saldoPendienteRecibo - sumaSeleccionados);
          const montoInicial = roundMoney(Math.min(d.montoPendiente, Math.max(0, saldoRestantePrevio)));

          return {
            ...m,
            [nroForm]: montoInicial,
          };
        });
      }
      return next;
    });
  };

  const setMontoAplicar = (nroForm: number, value: number, maxPendiente: number) => {
    setMontosAplicar((prev) => {
      const v = roundMoney(Math.max(0, value));
      const sumaOtros = Array.from(seleccionados).reduce((sum, nro) => {
        if (nro === nroForm) return sum;
        const mv = prev[nro];
        return sum + (typeof mv === 'number' ? mv : 0);
      }, 0);

      const saldoRestante = roundMoney(saldoPendienteRecibo - sumaOtros);
      const capped = roundMoney(Math.min(v, maxPendiente, saldoRestante));
      return { ...prev, [nroForm]: capped };
    });
  };

  const totalAAplicar = roundMoney(
    debitosParaAplicar
      .filter((d) => seleccionados.has(d.nroFormulario))
      .reduce((sum, d) => {
        const m = montosAplicar[d.nroFormulario];
        const val = typeof m === 'number' ? Math.min(Math.max(m, 0), d.montoPendiente) : 0;
        return sum + val;
      }, 0)
  );

  const saldoRestante = Math.max(0, roundMoney(saldoPendienteRecibo - totalAAplicar));

  const puedeAplicar = !reciboAnulado && totalAAplicar > 0 && totalAAplicar <= saldoPendienteRecibo;

  const handleAplicar = async () => {
    if (reciboAnulado) {
      enqueueSnackbar('No se puede aplicar saldo sobre un recibo anulado.', { variant: 'warning' });
      return;
    }
    if (!puedeAplicar) return;
    try {
      setAplicando(true);
      const docs: DocumentoAplicar[] = debitosParaAplicar
        .filter((d) => seleccionados.has(d.nroFormulario))
        .map((d) => {
          const m = montosAplicar[d.nroFormulario];
          const val = typeof m === 'number' ? Math.min(Math.max(m, 0), d.montoPendiente) : 0;
          return { nroIdformulario: d.nroFormulario, montoCancelado: roundMoney(val) };
        })
        .filter((x) => x.montoCancelado > 0);
      if (docs.length === 0) {
        enqueueSnackbar('No hay montos válidos para aplicar.', { variant: 'warning' });
        return;
      }
      if (isProveedor) {
        await tesoreriaAplicacionesService.aplicarSaldoPagoProveedor(
          reciboId,
          docs,
          creditoRecibo?.nroTipoFormulario ?? nroTipoFormularioCreditoProp
        );
      } else {
        await tesoreriaAplicacionesService.aplicarSaldoRecibo(reciboId, docs);
      }
      enqueueSnackbar('Saldo aplicado correctamente.', { variant: 'success' });
      await loadData();
      onSuccess?.();
      setTab(0);
    } catch (error: any) {
      const msg = error.response?.data?.message || 'Error al aplicar saldo.';
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setAplicando(false);
    }
  };

  const isDesaplicando = (app: AplicacionRecibo) =>
    desaplicando?.nroIdformulario === app.nroIdformulario &&
    desaplicando?.nroMovCaja === app.nroMovCaja;

  return (
    <>
      {pdfUrl && selectedCreditoPdfId && (
        <PDFViewer
          pdfUrl={pdfUrl}
          onClose={handleClosePDF}
          title={isProveedor ? `Orden de pago ${selectedCreditoPdfId}` : `Recibo ${selectedCreditoPdfId}`}
          documentId={`${selectedCreditoPdfId}`}
          downloadFileName={pdfFileName ?? undefined}
        />
      )}
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        TransitionProps={{ onExited: resetEstadoAlCerrar }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            {enSubvistaSelector && (
              <Button
                size="small"
                onClick={handleVolverAlSelector}
                sx={{ textTransform: 'none', flexShrink: 0 }}
              >
                {isProveedor ? '← Volver a créditos' : '← Volver a recibos'}
              </Button>
            )}
            <span>
              {modoVistaDebito
                ? `Imputaciones recibidas — ${nombreCliente}`
                : `Aplicar / Ver saldo - ${nombreCliente}`}
            </span>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {modoVistaDebito ? (
            loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Box>
                <BannerComprobanteSaldo
                  etiqueta={etiquetaComprobanteDebito(nroTipoFormularioDebitoCliente)}
                  numero={numeroComprobanteDebito}
                  saldoPendiente={saldoPendienteDebito}
                />
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {isProveedor
                    ? 'Créditos (órdenes de pago, notas de crédito, etc.) que aplicaron saldo a este comprobante.'
                    : 'Créditos (recibos, notas de crédito, etc.) que aplicaron saldo a este comprobante.'}
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Crédito</TableCell>
                        <TableCell>Fecha aplicación</TableCell>
                        <TableCell align="right">Monto aplicado</TableCell>
                        <TableCell align="center">Acciones</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {aplicacionesCreditoEnDebito.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4} align="center">
                            No hay imputaciones registradas para este documento.
                          </TableCell>
                        </TableRow>
                      ) : (
                        aplicacionesCreditoEnDebito.map((app) => {
                          const ck = claveImputaDesdeDebito(app);
                          return (
                            <TableRow key={ck}>
                              <TableCell>
                                {tipoToPrefijo(app.nroTipoFormularioCredito)}{' '}
                                {formatComprobante(app.letra, app.nroSuc, app.nroComprobante)}
                              </TableCell>
                              <TableCell>
                                {app.fechaAplicacion
                                  ? formatterFecha.format(new Date(app.fechaAplicacion))
                                  : '-'}
                              </TableCell>
                              <TableCell align="right">{formatCurrency(app.montoCancelado ?? 0)}</TableCell>
                              <TableCell align="center">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDesaplicarCreditoEnDebito(app)}
                                  disabled={desaplicandoDebitoClave === ck}
                                  title="Desaplicar"
                                >
                                  {desaplicandoDebitoClave === ck ? (
                                    <CircularProgress size={20} />
                                  ) : (
                                    <UndoIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )
          ) : (
            <>
          {reciboAnulado && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Este recibo está anulado. Solo puede consultar aplicaciones históricas; no es posible aplicar saldo.
            </Alert>
          )}

          {reciboId > 0 && numeroComprobanteCredito !== '-' && (
            <BannerComprobanteSaldo
              etiqueta="Comprobante de crédito"
              numero={numeroComprobanteCredito}
              saldoPendiente={saldoPendienteRecibo}
              mostrarSaldo={!reciboAnulado}
            />
          )}

        {!(selectorModeVista && reciboId === 0) && (
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
            <Tab label="Aplicaciones actuales" />
            <Tab label="Aplicar saldo" disabled={reciboAnulado || saldoPendienteRecibo <= 0} />
          </Tabs>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : selectorModeVista && reciboId === 0 ? (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {isProveedor
                ? 'Seleccione una orden de pago o nota de crédito con saldo pendiente para gestionar sus aplicaciones:'
                : 'Seleccione un recibo o nota de crédito con saldo pendiente para gestionar sus aplicaciones:'}
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Tipo</TableCell>
                    <TableCell>Comprobante</TableCell>
                    <TableCell>Fecha</TableCell>
                    <TableCell align="right">Saldo pendiente</TableCell>
                    <TableCell align="center">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {creditos.filter((cred) => !esDevolucionCobranzaCliente(cred.nroTipoFormulario ?? '', '')).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        {isProveedor
                          ? 'No hay órdenes de pago ni notas de crédito con saldo pendiente'
                          : 'No hay recibos ni notas de crédito con saldo pendiente'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    creditos
                      .filter((cred) => !esDevolucionCobranzaCliente(cred.nroTipoFormulario ?? '', ''))
                      .map((cred) => (
                      <TableRow key={cred.nroFormulario}>
                        <TableCell>{tipoToPrefijo(cred.nroTipoFormulario)}</TableCell>
                        <TableCell>{formatNumeroCredito(cred)}</TableCell>
                        <TableCell>{cred.fechaEmision ? formatterFecha.format(new Date(cred.fechaEmision)) : '-'}</TableCell>
                        <TableCell align="right">{formatCurrency(cred.saldoPendiente)}</TableCell>
                        <TableCell align="center">
                          <Stack direction="row" spacing={0.5} justifyContent="center">
                            {((!isProveedor && cred.nroTipoFormulario === '4') ||
                              (isProveedor && cred.nroTipoFormulario === '6')) && (
                              <Tooltip title="Imprimir PDF">
                                <IconButton
                                  onClick={() => handleImprimirPDF(cred.nroFormulario)}
                                  size="small"
                                  sx={{
                                    bgcolor: '#2196f3',
                                    color: '#fff',
                                    borderRadius: 1.5,
                                    width: 32,
                                    height: 32,
                                    '&:hover': { bgcolor: '#1976d2', transform: 'scale(1.1)' },
                                    transition: 'all 0.2s',
                                  }}
                                >
                                  <PrintIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="Aplicar saldo / Ver aplicaciones">
                              <IconButton
                                onClick={() => {
                                  setReciboSeleccionado(cred.nroFormulario);
                                }}
                                size="small"
                                sx={{
                                  bgcolor: '#2e7d32',
                                  color: '#fff',
                                  borderRadius: 1.5,
                                  width: 32,
                                  height: 32,
                                  '&:hover': { bgcolor: '#1b5e20', transform: 'scale(1.1)' },
                                  transition: 'all 0.2s',
                                }}
                              >
                                <AccountBalanceWalletIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        ) : (
          <>
            {tab === 0 && (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Comprobante</TableCell>
                      <TableCell>Fecha</TableCell>
                      <TableCell align="right">Monto aplicado</TableCell>
                      <TableCell align="center">Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {aplicaciones.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          No hay aplicaciones
                        </TableCell>
                      </TableRow>
                    ) : (
                      aplicaciones.map((app) => (
                        <TableRow key={`${app.nroIdformulario}-${app.fechaEmision}-${app.nroMovCaja}`}>
                          <TableCell>
                            {tipoToPrefijo(app.nroTipoFormulario)}{' '}
                            {formatComprobante(app.letra, app.nroSuc ?? 0, app.nroComprobante)}
                          </TableCell>
                          <TableCell>{app.fecha ? formatterFecha.format(new Date(app.fecha)) : '-'}</TableCell>
                          <TableCell align="right">{formatCurrency(app.montoCancelado ?? 0)}</TableCell>
                          <TableCell align="center">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDesaplicar(app)}
                              disabled={isDesaplicando(app)}
                              title="Desaplicar"
                            >
                              {isDesaplicando(app) ? (
                                <CircularProgress size={20} />
                              ) : (
                                <UndoIcon fontSize="small" />
                              )}
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {tab === 1 && (
              <Box>
                <Box
                  sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 5,
                    bgcolor: 'background.paper',
                    borderBottom: 1,
                    borderColor: 'divider',
                    mb: 1,
                    pb: 1,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, pt: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      {isProveedor
                        ? 'Seleccione documentos de deuda y montos a aplicar.'
                        : 'Seleccione facturas, devoluciones y montos a aplicar.'}{' '}
                      Total a aplicar: {formatCurrency(totalAAplicar)} / Saldo disponible: {formatCurrency(saldoRestante)}
                    </Typography>
                    <Box sx={{ flexShrink: 0 }}>
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={handleAplicar}
                        disabled={!puedeAplicar || aplicando}
                        sx={{ whiteSpace: 'nowrap' }}
                      >
                        {aplicando ? 'Aplicando...' : 'Aplicar saldo'}
                      </Button>
                    </Box>
                  </Box>
                  {totalAAplicar > saldoPendienteRecibo && (
                    <Alert severity="warning" sx={{ mt: 1, mb: 0 }}>
                      El total a aplicar supera el saldo disponible.
                    </Alert>
                  )}
                </Box>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox" />
                        <TableCell>Comprobante</TableCell>
                        <TableCell>Fecha</TableCell>
                        <TableCell align="right">Saldo pendiente</TableCell>
                        <TableCell align="right">Monto a aplicar</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {debitosParaAplicar.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} align="center">
                            {isProveedor
                              ? 'No hay documentos de deuda con saldo pendiente'
                              : 'No hay facturas ni devoluciones con saldo pendiente'}
                          </TableCell>
                        </TableRow>
                      ) : (
                        debitosParaAplicar.map((d) => (
                          <TableRow key={d.nroFormulario}>
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={seleccionados.has(d.nroFormulario)}
                                onChange={() => toggleSeleccion(d)}
                              />
                            </TableCell>
                            <TableCell>
                              {tipoToPrefijo(d.nroTipoFormulario)}{' '}
                              {formatComprobante(d.letra, d.nroSuc, d.nroComprobante ?? 0)}
                            </TableCell>
                            <TableCell>{d.fechaEmision ? formatterFecha.format(new Date(d.fechaEmision)) : '-'}</TableCell>
                            <TableCell align="right">{formatCurrency(d.montoPendiente)}</TableCell>
                            <TableCell align="right">
                              <TextField
                                type="number"
                                size="small"
                                value={montosAplicar[d.nroFormulario] ?? 0}
                                onChange={(e) => setMontoAplicar(d.nroFormulario, Number(e.target.value) || 0, d.montoPendiente)}
                                disabled={!seleccionados.has(d.nroFormulario)}
                                inputProps={{ min: 0, max: d.montoPendiente, step: 0.01 }}
                                InputProps={{
                                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                  inputProps: { style: { width: 100 } },
                                }}
                              />
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </>
        )}
      </>
    )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AplicarSaldoReciboDialog;
